<template>
    <section class="attachments-tab">
        <p v-if="loading" role="status">{{ $t("Loading") }}...</p>
        <p v-else-if="error" role="alert">{{ error }}</p>
        <p v-else-if="!files.length">{{ $t("NoAttachmentTitle") }}</p>
        <div v-else class="attachments-layout">
            <ul class="attachments-list">
                <li v-for="file in files" :key="`${file.source}-${file.id}`">
                    <button type="button" :class="{ selected: selected === file }" @click="open(file)">
                        <feather-icon icon="PaperclipIcon" /> {{ file.name }}
                    </button>
                </li>
            </ul>
            <div class="attachment-preview" v-if="selected">
                <header>
                    <strong>{{ selected.name }}</strong>
                    <div class="attachment-actions">
                        <b-button v-if="kind === 'model'" variant="outline-primary" :disabled="!modelReady" @click="resetModel">
                            <feather-icon icon="MaximizeIcon" class="mr-50" />{{ $t("AttachmentResetView") }}
                        </b-button>
                        <b-button variant="primary" @click="download" :disabled="opening || downloading">
                            <feather-icon icon="DownloadIcon" class="mr-50" />{{ $t("AttachmentDownload") }}
                        </b-button>
                    </div>
                </header>
                <p v-if="opening" role="status">{{ $t("Loading") }}...</p>
                <p v-else-if="previewError" role="alert">{{ previewError }}</p>
                <PdfPreview v-else-if="kind === 'pdf'" :key="url" :url="url" @error="unsupported" />
                <iframe ref="model" v-else-if="kind === 'model'" :key="url" :src="modelUrl" :title="selected.name" class="model-preview" />
                <img v-else-if="kind === 'image'" :src="url" :alt="selected.name" @error="unsupported" />
                <MediaPreview v-else-if="kind === 'audio' || kind === 'video'" :key="url" :kind="kind" :url="url" @error="unsupported" />
                <pre v-else-if="kind === 'text'">{{ text }}</pre>
                <p v-else>{{ $t("AttachmentPreviewUnavailable") }}</p>
            </div>
        </div>
    </section>
</template>

<script>
import { BButton } from "bootstrap-vue";
import MediaPreview from "./MediaPreview.vue";
import PdfPreview from "./PdfPreview.vue";

export default {
    components: { BButton, PdfPreview, MediaPreview },
    props: {
        operationId: { type: [Number, String], required: true },
        resourceId: { type: [Number, String], default: null }
    },
    data: () => ({ files: [], modelReady: false, downloading: false, loading: true, error: "", selected: null, opening: false, url: "", kind: "", text: "", previewError: "" }),
    computed: {
        modelUrl() {
            return `${process.env.BASE_URL}model3d/index.html#${encodeURIComponent(JSON.stringify({
                url: this.url, name: this.selected.name, loading: this.$t("Loading"),
                error: this.$t("AttachmentPreviewUnavailable"),
                help: this.$t("Attachment3DHelp")
            }))}`;
        }
    },
    async mounted() {
        window.addEventListener("message", this.modelMessage);
        try {
            const { data } = await this.$http2.get(`/api/mysfc/operations/${this.operationId}/attachments`, {
                params: { resourceId: this.resourceId || undefined }
            });
            this.files = data.data.filter(file => file.fileType !== "Link");
        } catch (error) {
            this.error = this.$t("AttachmentLoadError");
        } finally {
            this.loading = false;
        }
    },
    beforeDestroy() { this.releaseUrl(); window.removeEventListener("message", this.modelMessage); },
    methods: {
        modelMessage(event) {
            if (event.origin === window.location.origin && this.$refs.model && event.source === this.$refs.model.contentWindow)
                this.modelReady = event.data === "paperless-model-ready";
        },
        resetModel() { this.$refs.model.contentWindow.postMessage("paperless-model-fit", window.location.origin); },
        releaseUrl() {
            if (this.url.startsWith("blob:")) URL.revokeObjectURL(this.url);
            this.url = "";
        },
        unsupported() { this.previewError = this.$t("AttachmentPreviewUnavailable"); },
        async open(file) {
            this.selected = file;
            this.modelReady = false;
            this.opening = true;
            this.previewError = "";
            this.kind = "";
            this.releaseUrl();
            const extension = file.name.split(".").pop().toLowerCase();
            try {
                let url;
                let kind = "";
                if (["doc", "docx", "odt", "rtf", "xls", "xlsx", "ods", "ppt", "pptx", "odp"].includes(extension)) {
                    const { data } = await this.$http2.get("/api/attachments/preview", { params: { path: file.url }, responseType: "blob" });
                    url = URL.createObjectURL(data);
                    kind = "pdf";
                } else if (!["pdf", "stl", "step", "stp", "obj"].includes(extension) && file.url.includes("/sfc-paperless/")) {
                    const { data } = await this.$http2.get("/api/attachments/access", { params: { path: file.url } });
                    url = data.data.url;
                } else {
                    const { data } = await this.$http2.get("/api/attachments/content", { params: { path: file.url }, responseType: "blob" });
                    url = URL.createObjectURL(data);
                }
                if (this.selected !== file) { if (url.startsWith("blob:")) URL.revokeObjectURL(url); return; }
                this.url = url;
                this.kind = kind;
                if (!this.kind) {
                    if (extension === "pdf") this.kind = "pdf";
                    else if (["stl", "step", "stp", "obj"].includes(extension)) this.kind = "model";
                    else if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "avif"].includes(extension)) this.kind = "image";
                    else if (["mp3", "wav", "m4a", "aac", "ogg", "flac"].includes(extension)) this.kind = "audio";
                    else if (["mp4", "webm", "ogv", "mov", "mpeg", "wmv", "avi"].includes(extension)) this.kind = "video";
                    else if (["txt", "csv", "json", "xml", "md", "log", "html", "css", "js", "yaml", "yml"].includes(extension)) {
                        const { data } = await this.$http2.get("/api/attachments/content", { params: { path: file.url }, responseType: "blob" });
                        const text = await data.text();
                        if (this.selected !== file) return;
                        this.text = text;
                        this.kind = "text";
                    }
                }
            } catch (error) {
                if (this.selected === file) this.previewError = this.$t("AttachmentPreviewUnavailable");
            } finally {
                if (this.selected === file) this.opening = false;
            }
        },
        async download() {
            this.downloading = true;
            const file = this.selected;
            try {
                const { data } = await this.$http2.get("/api/attachments/content", { params: { path: file.url }, responseType: "blob" });
                const url = URL.createObjectURL(data);
                const link = document.createElement("a");
                link.href = url;
                link.download = file.name;
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (error) { this.previewError = this.$t("AttachmentLoadError"); }
            finally { this.downloading = false; }
        }
    }
};
</script>

<style scoped lang="scss">
.attachments-tab { padding: 20px; height: 100%; overflow: auto; }
.attachments-layout { display: flex; gap: 20px; height: 100%; }
.attachments-list { list-style: none; padding: 0; width: 240px; flex-shrink: 0; overflow: auto; }
.attachments-list button { padding: 12px; border: 1px solid #cfc4be; background: transparent; border-radius: 6px; color: inherit; }
.attachments-list button { width: 100%; text-align: left; margin-bottom: 8px; overflow-wrap: anywhere; }
.attachments-list button.selected { border-color: #974900; background: #fff3e8; }
.attachment-preview { flex: 1; min-width: 0; }
header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; overflow-wrap: anywhere; }
img { max-width: 100%; max-height: 65vh; }
.attachment-actions { display: flex; flex-wrap: wrap; gap: 8px; }
header { flex-wrap: wrap; }
.model-preview { width: 100%; height: 65vh; min-height: 320px; border: 0; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 65vh; overflow: auto; }
@media (max-width: 767px) {
    .attachments-layout { flex-direction: column; height: auto; }
    .attachments-list { width: 100%; max-height: 180px; }
}
</style>

<style lang="scss">
.attachments-tab {
    .btn-primary, .btn-outline-primary {
        border-color: #974900 !important;
        &:focus, &:hover, &:active { box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.2) !important; }
    }
    .btn-primary { background-color: #974900 !important; color: #fff !important; }
    .btn-outline-primary {
        color: #974900 !important;
        background-color: transparent !important;
        &:hover:not(:disabled), &:active:not(:disabled) { background-color: #974900 !important; color: #fff !important; }
    }
    .custom-range {
        &::-webkit-slider-thumb { background-color: #974900 !important; }
        &::-moz-range-thumb { background-color: #974900 !important; }
        &:focus::-webkit-slider-thumb { box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.2) !important; }
        &:focus::-moz-range-thumb { box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.2) !important; }
    }
}
</style>
